import { ConnectProto } from "@racoon/api";
import { Namespace, useT } from "@racoon/i18n";
import { type FilterCategoryItem, type FilterStateItem, useUrlFilters } from "@racoon/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import { isNumericId } from "@racoon/lib";
import { useListCustomerTypesQuery } from "~/shared/api/organization-labels/organization-labels.grpc";
import { assignableLabels } from "~/shared/lib/organization-labels";

const ALL_STATUSES = "all";
const ALL_TYPES = "all";
export const CUSTOMERS_PAGE_SIZE = 100;

/**
 * The `search` ListCounterparties gets for what was typed: trimmed. The server reads a
 * digits-only query as a BIN prefix and anything else as part of the name, so a whole BIN
 * (12 digits) pasted with spaces inside it ("1234 5678 9012") is sent as its digits, or it
 * would search names. Shorter space-separated digits ("2 5") stay a name search.
 */
export const toCounterpartySearch = (query: string): string => {
  const trimmed = query.trim();
  const digits = trimmed.replace(/\s+/g, "");
  return /^\d{12}$/.test(digits) ? digits : trimmed;
};

/**
 * @param providerId whose customer types the type pills offer.
 */
export const useCustomersFilters = (providerId?: number | string | null) => {
  const t = useT();
  const { sp, patch } = useUrlFilters();

  // Type pills: the provider's customer types by id, "0" for customers with none.
  const typeParam = sp.get("customer_type") ?? "";
  const typeValue = isNumericId(typeParam) ? typeParam : ALL_TYPES;
  const onCategoryChange = (value: string) =>
    patch({ customer_type: value === ALL_TYPES ? null : value });

  const { currentData: typesResponse } = useListCustomerTypesQuery(
    { ownerOrganizationIds: providerId ? [BigInt(providerId)] : [] },
    { skip: !providerId },
  );
  // "Все", «Не указано», then the active types in the settings' order; an archived
  // type keeps its pill while it is the one selected. With no types at all every
  // customer is «Не указано», so there is nothing to narrow and no pills are shown,
  // unless the URL already filters by type: «Все» must stay to clear it.
  const categories: FilterCategoryItem[] = useMemo(() => {
    const types = assignableLabels(typesResponse?.labels, typeValue);
    if (types.length === 0 && typeValue === ALL_TYPES) return [];
    return [
      { key: t(Namespace.Enum, "ALL"), value: ALL_TYPES },
      { key: t(Namespace.Settings, "organizationLabels.undefined"), value: "0" },
      ...types.map((label) => ({ key: label.name, value: label.id.toString() })),
    ];
  }, [t, typesResponse, typeValue]);

  const statusValue = sp.get("status") ?? ConnectProto.ContractStatus.ACTIVE.toString();
  // Older links used ?search=; reads honor it, writes stay on ?q=.
  const searchQuery = sp.get("q") ?? sp.get("search") ?? "";

  const [searchValue, setSearchValue] = useState(searchQuery);
  useEffect(() => {
    setSearchValue(searchQuery);
  }, [searchQuery]);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  const onSearchChange = (value: string) => {
    setSearchValue(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      // `search` is the legacy key this page still reads; clear it alongside `q` or a
      // cleared input would be re-seeded from the old link on the next render.
      patch({ q: value.trim() ? value : null, search: null });
    }, 1000);
  };

  const onStateChange = (state: FilterStateItem) => patch({ status: state.value });

  const states: FilterStateItem[] = useMemo(
    () => [
      {
        key: t(Namespace.Enum, "CONTRACT_STATUS_ACTIVE"),
        value: ConnectProto.ContractStatus.ACTIVE.toString(),
      },
      {
        key: t(Namespace.Enum, "CONTRACT_STATUS_INACTIVE"),
        value: ConnectProto.ContractStatus.INACTIVE.toString(),
      },
      { key: t(Namespace.Enum, "ALL"), value: ALL_STATUSES },
    ],
    [t],
  );

  const selectedState = states.find((state) => state.value === statusValue) ?? states[0];

  const contractStatus =
    selectedState.value === ALL_STATUSES
      ? undefined
      : (Number(selectedState.value) as ConnectProto.ContractStatus);

  return {
    contractStatus,
    customerTypeId: typeValue === ALL_TYPES ? undefined : BigInt(typeValue),
    categories,
    selectedCategory: typeValue,
    onCategoryChange,
    states,
    selectedState,
    onStateChange,
    searchValue,
    searchQuery,
    onSearchChange,
  };
};
